import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@config/store';
import { authInfoSelect } from '@entities/auth';
import { clientSelect, setSelectedClient } from '@entities/client';
import { clearCardState } from '@entities/card';
import {
    getAllCardsSpendStats,
    getClientCardChanges,
    getClientCards,
    getClientCardTransactions,
} from '@api/thunks/card';
import { IClient, IPaginatedProps } from '@types';
import { getClient } from '@api/thunks/clients';

interface UseClientActionsProps {
    transPagination: IPaginatedProps;
    changesPagination: IPaginatedProps;
    setOpenedSettingsPopup: (isOpen: boolean) => void;
}

export const useClientActions = ({
    transPagination,
    changesPagination,
    setOpenedSettingsPopup,
}: UseClientActionsProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { shop } = useSelector(authInfoSelect);
    const { clients } = useSelector(clientSelect);

    const handleShowClientModal = useCallback(
        (isShow: boolean) => {
            setOpenedSettingsPopup(isShow);
            if (!isShow) {
                dispatch(clearCardState());
            }
        },
        [dispatch, setOpenedSettingsPopup]
    );

    const getClientData = useCallback(
        (findClient: IClient) => {
            if (!shop) return;

            dispatch(setSelectedClient(findClient));
            setOpenedSettingsPopup(true);

            const data = { client_id: findClient.id, shop_id: shop.id };

            const { limit, page } = transPagination;
            const query = { limit, offset: (page - 1) * limit };
            dispatch(getClientCardTransactions({ ...data, query }));

            const { limit: limitChanges, page: pageChanges } =
                changesPagination;
            const queryChanges = {
                limit: limitChanges,
                offset: (pageChanges - 1) * limitChanges,
            };
            dispatch(getClientCardChanges({ ...data, query: queryChanges }));

            dispatch(getClientCards(data));
            dispatch(
                getAllCardsSpendStats({
                    shop_id: shop.id,
                    client_id: findClient.id,
                })
            );
        },
        [
            shop,
            dispatch,
            setOpenedSettingsPopup,
            transPagination,
            changesPagination,
        ]
    );

    const handlerClickId = useCallback(
        (id: number, e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            e.preventDefault();
            if (!shop) return;

            let findClient = clients?.find(o => o.id === id);

            if (!findClient) {
                dispatch(getClient({ shop_id: shop.id, client_id: id })).then(
                    res => {
                        if (
                            res.meta.requestStatus === 'fulfilled' &&
                            res.payload
                        ) {
                            findClient = res.payload as IClient;
                            getClientData(findClient);
                        }
                    }
                );
            } else {
                getClientData(findClient);
            }
        },
        [clients, shop, dispatch, getClientData]
    );

    return {
        handleShowClientModal,
        handlerClickId,
    };
};
